import { useEffect, useState } from "react";
import { Loader2, Mail, ArrowLeft, RotateCcw, ShieldCheck } from "lucide-react";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { toast } from "sonner";
import api from "../../lib/api";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";

export const WORK_STATUSES = [["employed", "Employed"], ["looking", "Looking for a new role"], ["unemployed", "Unemployed"]];

export const errText = (e, fallback = "Something went wrong. Please try again.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ") || fallback;
  return fallback;
};

export function WorkStatusSelect({ value, onChange, id, testid = "work-status-select" }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger id={id} data-testid={testid} className="h-12 rounded-xl border-line-strong/70 bg-surface text-base text-ink">
        <SelectValue placeholder="Choose your work status" />
      </SelectTrigger>
      <SelectContent className="bg-surface border-line text-ink z-[80]">
        {WORK_STATUSES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-${v}`} className="text-[15px] py-2.5">{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function CodeEntry({ sentTo, onVerify, onResend, onBack, cta = "Verify", testid = "code-entry" }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(60);
  useEffect(() => { if (!wait) return; const t = setTimeout(() => setWait((s) => s - 1), 1000); return () => clearTimeout(t); }, [wait]);

  const verify = async (c = code) => {
    if (c.length !== 6 || busy) return;
    setBusy(true);
    try { await onVerify(c); } catch (e) { toast.error(errText(e, "That code didn't work. Please try again.")); setCode(""); setBusy(false); }
  };
  const resend = async () => {
    try { await onResend(); setWait(60); setCode(""); toast.success("We've sent you a new code."); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-4" data-testid={testid}>
      <p className="text-[15px] text-body" data-testid={`${testid}-sent-to`}>
        We've emailed a 6-digit code to <span className="font-semibold text-ink">{sentTo}</span>. It expires in 10 minutes.
      </p>
      <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={verify} pattern={REGEXP_ONLY_DIGITS} autoFocus disabled={busy}
        data-testid={`${testid}-input`} aria-label="6-digit code" containerClassName="justify-start">
        <InputOTPGroup>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <InputOTPSlot key={i} index={i} className="h-12 w-11 sm:w-12 text-lg font-semibold text-ink bg-surface border-line-strong/70" />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <button data-testid={`${testid}-submit`} onClick={() => verify()} disabled={busy || code.length !== 6} className="w-full gi-btn gi-btn-primary gi-btn-lg disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {cta}
      </button>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <button data-testid={`${testid}-resend`} onClick={resend} disabled={wait > 0} className="inline-flex items-center gap-1.5 font-medium text-brand hover:text-brand-hover disabled:text-faint disabled:cursor-not-allowed">
          <RotateCcw className="w-4 h-4" aria-hidden="true" /> {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
        </button>
        {onBack && (
          <button data-testid={`${testid}-back`} onClick={onBack} className="inline-flex items-center gap-1.5 font-medium text-body hover:text-ink">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Use a different email
          </button>
        )}
      </div>
      <p className="text-xs text-faint">No email? Check your spam or promotions folder.</p>
    </div>
  );
}

export function EmailCodeSignIn({ withStatus = false, onDone, cta = "Verify and continue", testid = "email-code" }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const send = () => api.post("/auth/code/send", { email: email.trim(), work_status: status });

  const start = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return toast.error("Please enter a valid email address.");
    if (withStatus && !status) return toast.error("Choose your work status first.");
    setSending(true);
    try { const { data } = await send(); setSentTo(data.sent_to); setStep("code"); } catch (e) { toast.error(errText(e)); } finally { setSending(false); }
  };
  if (step === "code") return (
    <CodeEntry sentTo={sentTo} cta={cta} testid={`${testid}-code`} onBack={() => setStep("email")} onResend={send}
      onVerify={async (code) => { const { data } = await api.post("/auth/code/verify", { email: email.trim(), code }); await onDone?.(data); }} />
  );
  return (
    <div className="space-y-4" data-testid={`${testid}-form`}>
      <div>
        <label htmlFor={`${testid}-email`} className="gi-label">Email</label>
        <input id={`${testid}-email`} data-testid={`${testid}-email-input`} type="email" autoComplete="email" value={email} maxLength={254}
          onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !withStatus && start()} placeholder="you@email.com" className="gi-input h-12" />
      </div>
      {withStatus && (
        <div>
          <label htmlFor={`${testid}-status`} className="gi-label">Work status</label>
          <WorkStatusSelect id={`${testid}-status`} value={status} onChange={setStatus} testid={`${testid}-status`} />
        </div>
      )}
      <button data-testid={`${testid}-send`} onClick={start} disabled={sending} className="w-full gi-btn gi-btn-primary gi-btn-lg">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" aria-hidden="true" />} Email me a code
      </button>
      <p className="flex items-start gap-2 text-sm text-body">
        <ShieldCheck className="w-4 h-4 text-kesh mt-0.5 shrink-0" aria-hidden="true" /> We email you a one-time code to confirm it's really you. No password needed.
      </p>
    </div>
  );
}
